import { useEffect, useRef, useState } from "react";
import { TaskStatus, Task, TaskColumns } from "../shared/types/Task";
import { sortTasks } from "../helpers/tasksHelpers";
import { useAppSelector } from "./useAppSelector";
import { useFilterTasksHook } from "./useFilterTasksHook";
import { useAppDispatch } from "./useAppDispatch";
import { startUpdatingTask } from "../store/thunks/taskThunks";
import {
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

const ALL_STATUSES = Object.values(TaskStatus);

export const useKanban = () => {
  const dispatch = useAppDispatch();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const { tasks } = useAppSelector((state) => state.taskReducer);
  const { filters, filteredTasks, handleFilterChange } =
    useFilterTasksHook(tasks);

  const sortedTasks = sortTasks(filteredTasks);
  const [columns, setColumns] = useState<TaskColumns>(
    getInitialColumns(sortedTasks)
  );
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  // Always-fresh ref so event handlers never see stale columns
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const sourceColumnRef = useRef<TaskStatus | null>(null);

  useEffect(() => {
    setColumns(getInitialColumns(sortTasks(filteredTasks)));
  }, [filteredTasks]);

  function getInitialColumns(sorted: Task[]): TaskColumns {
    return Object.keys(TaskStatus).reduce((acc, key) => {
      const status = TaskStatus[key as keyof typeof TaskStatus];
      acc[status] = sorted.filter((task) => task.status === status);
      return acc;
    }, {} as TaskColumns);
  }

  const onDragStart = (event: DragStartEvent) => {
    const taskId = event.active.id as string;

    for (const status of ALL_STATUSES) {
      const task = columnsRef.current[status].find((t) => t._id === taskId);
      if (task) {
        sourceColumnRef.current = status;
        setActiveTask(task);
        return;
      }
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const sourceColumn = sourceColumnRef.current;

    // Freeze scroll position during drop animation + re-render
    const container = document.querySelector(
      "[data-dashboard-scroll]"
    ) as HTMLElement | null;
    if (container) {
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;
      const lock = () => {
        container.scrollTop = scrollTop;
        container.scrollLeft = scrollLeft;
      };
      container.addEventListener("scroll", lock);
      setTimeout(() => container.removeEventListener("scroll", lock), 400);
    }

    setActiveTask(null);
    sourceColumnRef.current = null;

    if (!over || !sourceColumn) return;

    // over.id is always a TaskStatus string (columns are the only droppables)
    const targetColumn = over.id as TaskStatus;
    if (!ALL_STATUSES.includes(targetColumn)) return;
    if (sourceColumn === targetColumn) return;

    const activeId = active.id as string;

    // Move task in state
    setColumns((prev) => {
      const sourceItems = prev[sourceColumn];
      const taskIndex = sourceItems.findIndex((t) => t._id === activeId);
      if (taskIndex === -1) return prev;

      const task = { ...sourceItems[taskIndex], status: targetColumn };
      return {
        ...prev,
        [sourceColumn]: sourceItems.filter((_, i) => i !== taskIndex),
        [targetColumn]: [...prev[targetColumn], task],
      };
    });

    // Persist via API
    const task = columnsRef.current[sourceColumn]?.find(
      (t) => t._id === activeId
    );
    if (task) {
      updateTaskStatusAndDispatch({ ...task, status: targetColumn });
    }
  };

  const updateTaskStatusAndDispatch = async (task: Task) => {
    try {
      const updated = { ...task };
      if (shouldSetCompletedAt(updated.status, updated.completedAt)) {
        updated.completedAt = new Date().toISOString();
      } else {
        updated.completedAt = null;
      }

      await dispatch(startUpdatingTask(updated, updated._id!));
    } catch (error) {
      console.error(error);
    }
  };

  const shouldSetCompletedAt = (
    status: TaskStatus,
    completedAt: string | null | undefined
  ) =>
    (status === TaskStatus.Completed || status === TaskStatus.Canceled) &&
    !completedAt;

  return {
    sensors,
    columns,
    activeTask,
    onDragStart,
    onDragEnd,
    filters,
    handleFilterChange,
  };
};
